import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { getHostTenantSlug } from '../lib/hostTenant';

export const TenantContext = createContext(null);

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
};

const DEFAULT_THEME = {
  primary: '#4361EE', // NoiRock brand blue — preserved (cf. CLAUDE.md branding)
  accent: '#E5202A',  // logo red — rebrand 2026-04-30 (was legacy #7C3AED)
  bg: '#000000',      // pitch black — dark unified theme (was light #F4F6FB)
};

export const TenantProvider = ({ children }) => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tenant pinned by the hostname (e.g. tcn.noirock.app). Front-end branding
  // hint only — resolved once, before auth. See src/lib/hostTenant.js.
  const subdomainTenantSlug = useMemo(() => getHostTenantSlug(), []);

  const hydrate = useCallback(async () => {
    if (authLoading) return;
    if (!user || !userProfile) {
      setTenant(null);
      setAgency(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const tenantId = userProfile.tenant_id;
      const agencyId = userProfile.agency_id;

      if (!tenantId) {
        // Super admin or unassigned user — leave tenant null
        setTenant(null);
        setAgency(null);
        setLoading(false);
        return;
      }

      const [tenantRes, agencyRes] = await Promise.all([
        supabase.from('tenants').select('*').eq('id', tenantId).single(),
        agencyId
          ? supabase.from('agencies').select('*').eq('id', agencyId).single()
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (tenantRes.error) throw tenantRes.error;
      if (agencyRes.error) throw agencyRes.error;

      setTenant(tenantRes.data);
      setAgency(agencyRes.data);
    } catch (e) {
      console.error('TenantProvider hydrate error:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [authLoading, user, userProfile]);

  useEffect(() => { hydrate(); }, [hydrate]);

  // Pin TCN branding (theme + favicon) on a dedicated subdomain, even before
  // auth. data-theme is also toggled per-route by TcnThemeProvider; setting it
  // globally here means the whole tcn.* host is themed (login included). No
  // conflict: TcnThemeProvider captures prev='tcn' and restores to 'tcn'.
  useEffect(() => {
    if (subdomainTenantSlug !== 'tcn') return;
    document.documentElement.setAttribute('data-theme', 'tcn');
    // Favicon
    const icon = document.querySelector("link[rel~='icon']");
    if (icon) icon.setAttribute('href', '/favicon-tcn-32.png');
    // PWA identity → TCN (name + icons). Swap every manifest link (the static
    // one + the VitePWA-injected manifest.webmanifest) so the install prompt /
    // home-screen app shows "TCN" with the TCN logo, not NoiRock.
    document.querySelectorAll("link[rel='manifest']").forEach((l) => l.setAttribute('href', '/manifest-tcn.json'));
    const apple = document.querySelector("link[rel='apple-touch-icon']");
    if (apple) apple.setAttribute('href', '/apple-touch-icon-tcn.png');
    const appleTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
    if (appleTitle) appleTitle.setAttribute('content', 'TCN Agent');
    let appName = document.querySelector("meta[name='application-name']");
    if (!appName) {
      appName = document.createElement('meta');
      appName.setAttribute('name', 'application-name');
      document.head.appendChild(appName);
    }
    appName.setAttribute('content', 'TCN Agent');
    document.title = 'TCN Agent';

    // iOS PWA launch (splash) images — swapped in for the tcn.* home-screen app.
    // Each needs an exact device media query; non-matching devices fall back to
    // a blank launch screen (acceptable). data-tcn-splash marks them idempotent.
    const SPLASH = [
      ['/splash-tcn-iphone-se.png', '(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2)'],
      ['/splash-tcn-iphone-15-pro.png', '(device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3)'],
      ['/splash-tcn-iphone-15-pro-max.png', '(device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3)'],
    ];
    if (!document.querySelector('link[data-tcn-splash]')) {
      for (const [href, media] of SPLASH) {
        const l = document.createElement('link');
        l.setAttribute('rel', 'apple-touch-startup-image');
        l.setAttribute('href', href);
        l.setAttribute('media', media);
        l.setAttribute('data-tcn-splash', '');
        document.head.appendChild(l);
      }
    }
  }, [subdomainTenantSlug]);

  // Apply RTL direction + lang attribute
  useEffect(() => {
    const language = userProfile?.language || 'fr';
    const dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
  }, [userProfile?.language]);

  const value = useMemo(() => {
    const features = tenant?.features ?? {};
    const permissionMatrix = tenant?.permission_matrix ?? { pages: {}, actions: {} };
    const labels = tenant?.labels ?? {};
    const theme = { ...DEFAULT_THEME, ...(tenant?.theme ?? {}) };
    const servers = agency?.servers ?? [];
    const language = userProfile?.language || 'fr';

    return {
      // identity
      tenantId: tenant?.id ?? null,
      tenantSlug: tenant?.slug ?? null,
      tenantName: tenant?.name ?? null,

      // subdomain branding hint (pre-auth). effectiveTenantSlug favours the
      // resolved profile tenant, falling back to the hostname-pinned slug so
      // logo/theme are correct before login on a dedicated subdomain.
      subdomainTenantSlug,
      effectiveTenantSlug: (tenant?.slug ?? null) || subdomainTenantSlug,

      agencyId: agency?.id ?? null,
      agencyName: agency?.name ?? null,

      // role + permissions
      userRole: userProfile?.role ?? null,
      permissionMatrix,

      // features
      features,

      // ui
      theme,
      labels,
      language,
      direction: language === 'ar' ? 'rtl' : 'ltr',

      // operational
      servers,
      payoutConfig: agency?.payout_config ?? {},

      // raw rows (escape hatch)
      tenant,
      agency,

      // status
      loading,
      error,
      refresh: hydrate,
    };
  }, [tenant, agency, userProfile, loading, error, hydrate, subdomainTenantSlug]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
